import React, { useMemo, useCallback, useEffect } from "react";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  CompanyActionButtons,
  CompanyActionButtonsSkeleton,
} from "./CompanyActionButtons";
import {
  useCompanyPageStore,
  useHasPrevious,
  useHasNext,
} from "../../../store/companyPageStore";
import { TAB_DEFINITIONS, CompanyTab } from "../types";
import { useLocation, useParams } from "react-router-dom";
import {
  buildCompanyRouteContextSearch,
  getExploreSearchFromSearch,
} from "@/utils/companyRouteContext";
interface CompanyDrawerHeaderProps {
  onClose: () => void;
}

export const CompanyDrawerHeader: React.FC<CompanyDrawerHeaderProps> = ({
  onClose,
}) => {
  const location = useLocation();
  const { segmentId } = useParams<{ segmentId?: string }>();
  const companyId = useCompanyPageStore((state) => state.company?.hgId);
  const companyLoading = useCompanyPageStore(
    (state) => state.loadingStates.company
  );
  const activeTab = useCompanyPageStore((state) => state.activeTab);
  const navigateToPrevious = useCompanyPageStore(
    (state) => state.navigateToPrevious
  );
  const navigateToNext = useCompanyPageStore((state) => state.navigateToNext);
  const hasPrevious = useHasPrevious();
  const hasNext = useHasNext();

  const openInNewTabUrl = useMemo(() => {
    if (!companyId) return undefined;
    const tabPath = TAB_DEFINITIONS[activeTab as CompanyTab]?.path ?? "";
    const segmentContext = buildCompanyRouteContextSearch({
      segmentId,
      search: getExploreSearchFromSearch(location.search),
    });
    return `/catalog/companies/${companyId}${tabPath}${segmentContext}`;
  }, [companyId, activeTab, segmentId, location.search]);

  const handleOpenInNewTab = useCallback(() => {
    if (openInNewTabUrl) {
      window.open(openInNewTabUrl, "_blank", "noopener,noreferrer");
    }
  }, [openInNewTabUrl]);

  // Keyboard navigation: Arrow keys to navigate between companies
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
        return;
      if (event.repeat) return;

      const target = event.target instanceof Element ? event.target : null;
      const htmlTarget =
        event.target instanceof HTMLElement ? event.target : null;
      const tagName = target?.tagName ?? "";
      const hasIgnoredRole =
        target?.closest?.(
          '[role="textbox"],[role="combobox"],[role="listbox"],[role="menu"],[role="grid"],[role="tree"],[role="slider"]'
        ) != null;
      const shouldIgnore =
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        tagName === "SELECT" ||
        htmlTarget?.isContentEditable === true ||
        hasIgnoredRole;

      if (shouldIgnore) return;

      if (event.key === "ArrowLeft" && hasPrevious) {
        event.preventDefault();
        navigateToPrevious();
        return;
      }

      if (event.key === "ArrowRight" && hasNext) {
        event.preventDefault();
        navigateToNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasPrevious, hasNext, navigateToPrevious, navigateToNext]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ px: 2, py: 1 }}
    >
      {/* Navigation controls - always visible */}
      <Stack direction="row" spacing={0.5} flex={1} alignItems="center">
        <Tooltip title="Previous company">
          <IconButton
            data-testid="company-detail-nav-prev-btn"
            onClick={navigateToPrevious}
            disabled={!hasPrevious}
            size="small"
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" sx={{ height: 18, mx: 0.5 }} />
        <Tooltip title="Next company">
          <IconButton
            data-testid="company-detail-nav-next-btn"
            onClick={navigateToNext}
            disabled={!hasNext}
            size="small"
          >
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Action buttons with skeleton when loading */}
      <Stack direction="row" spacing={1}>
        {companyLoading || !companyId ? (
          <CompanyActionButtonsSkeleton />
        ) : (
          <CompanyActionButtons />
        )}
      </Stack>
      <Stack direction="row" spacing={0.5} marginLeft={4}>
        <Tooltip title="Open in new tab">
          <span>
            <IconButton
              data-testid="company-detail-open-newtab-btn"
              onClick={handleOpenInNewTab}
              disabled={!companyId || companyLoading}
              size="small"
              aria-label="Open company details in new tab"
            >
              <OpenInNewIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton
          data-testid="company-detail-close-btn"
          onClick={onClose}
          size="small"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>
    </Stack>
  );
};
